// ── 8-bit class sprites ────────────────────────────────────────────────────
// Sprites are drawn on a 16×16 logical grid, rendered to an off-screen canvas
// at the requested pixel size.  Colour key used by draw functions:
//   P  = player primary colour    D = darkened primary
//   S  = skin (#f0c090)           K = outline (#111)
//   W  = white (#eee)             G = silver (#b0b8c0)
//   Au = gold (#fbbf24)           Br = brown (#8b5e3c)
//   Db = dark brown (#5a3020)     Rd = red (#cc4444)
//   Gn = dark green (#2d7d32)     Pv = purple (#7c3aed)

const S  = '#f0c090';
const Sd = '#c07848';
const K  = '#111111';
const W  = '#eeeeee';
const G  = '#b0b8c0';
const Gd = '#7a8890';
const Au = '#fbbf24';
const Br = '#8b5e3c';
const Db = '#5a3020';
const Rd = '#cc4444';
const Gn = '#2d7d32';
const Pv = '#7c3aed';

function darken(hex, f = 0.5) {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return `rgb(${Math.round(r * f)},${Math.round(g * f)},${Math.round(b * f)})`;
}

// ── Sprite draw functions ──────────────────────────────────────────────────
// Each receives: (f, P, D)
//   f(x, y, w, h, color)  — fills a rect on the 16×16 logical grid
//   P                     — primary (player colour)
//   D                     — dark primary

const DEFS = {

  fighter(f, P, D) {
    f(5,1,6,1,G);    // helm top
    f(4,2,8,3,G);    // helm body
    f(5,3,6,1,S);    // visor/face
    f(6,3,1,1,K);f(9,3,1,1,K); // eyes
    f(2,5,3,2,G);    // left pauldron
    f(11,5,3,2,G);   // right pauldron
    f(4,5,8,5,P);    // chest plate (tabard)
    f(7,5,2,5,D);    // chest cross vertical
    f(4,8,8,1,D);    // chest cross horizontal
    f(4,10,8,1,Au);  // belt
    f(4,11,3,3,P);   // left leg
    f(9,11,3,3,P);   // right leg
    f(4,14,3,1,D);   // left boot
    f(9,14,3,1,D);   // right boot
    f(13,5,1,1,Au);  // sword pommel
    f(12,6,3,1,G);   // sword crossguard
    f(13,7,1,5,G);   // sword blade
  },

  wizard(f, P, D) {
    f(8,0,1,1,P);f(7,1,3,1,P);f(6,2,4,1,P); // hat tip
    f(5,3,6,1,P);f(4,4,8,1,P);               // hat lower
    f(3,4,10,1,D);                            // hat brim
    f(5,5,6,3,S);                             // face
    f(6,6,1,1,K);f(9,6,1,1,K);               // eyes
    f(7,7,2,1,Br);                            // beard start
    f(3,8,10,6,P);                            // robe
    f(5,9,2,1,Au);f(9,10,2,1,Au);            // robe stars
    f(7,10,2,2,Au);
    f(4,8,8,1,D);                             // sash
    f(4,13,3,2,P);f(9,13,3,2,P);             // feet
    f(2,4,1,10,Br);                           // staff
    f(1,3,3,2,Au);                            // staff crystal
  },

  rogue(f, P, D) {
    f(4,1,8,4,D);    // hood
    f(5,2,6,3,D);    // hood deeper shadow
    f(6,3,4,2,S);    // face inside hood
    f(7,3,1,1,K);f(9,3,1,1,K); // eyes (shadowed)
    f(3,5,10,1,D);   // shoulders
    f(2,6,12,1,D);   // cape spread
    f(4,7,8,4,P);    // body
    f(7,7,2,4,D);    // belt
    f(4,11,3,3,P);   // left leg
    f(9,11,3,3,P);   // right leg
    f(4,13,3,2,D);   // left boot
    f(9,13,3,2,D);   // right boot
    f(2,7,1,4,G);    // left dagger blade
    f(1,6,3,1,Br);   // left dagger handle
    f(13,7,1,4,G);   // right dagger blade
    f(12,6,3,1,Br);  // right dagger handle
  },

  cleric(f, P, D) {
    f(5,1,6,4,S);    // face
    f(5,1,6,1,Br);   // hair
    f(4,1,1,4,Br);f(11,1,1,4,Br); // hair sides
    f(6,2,1,1,K);f(9,2,1,1,K);    // eyes
    f(3,5,10,9,P);   // robe
    f(4,5,8,1,W);    // white collar
    f(4,5,4,1,P);    // collar gap
    f(8,6,1,4,Au);   // cross vertical
    f(7,8,3,1,Au);   // cross horizontal
    f(4,11,8,1,Au);  // belt
    f(5,12,2,3,P);   // left leg
    f(9,12,2,3,P);   // right leg
    f(13,4,1,1,G);   // mace head top
    f(12,5,3,2,G);   // mace head
    f(13,7,1,7,Br);  // mace haft
  },

  ranger(f, P, D) {
    f(4,0,8,2,D);    // ranger hat / hood brim
    f(5,2,6,3,S);    // face
    f(6,3,1,1,K);f(9,3,1,1,K); // eyes
    f(4,2,1,3,Br);f(11,2,1,3,Br); // hair
    f(4,5,8,5,P);    // body
    f(4,5,8,1,D);    // collar
    f(3,8,10,1,Br);  // leather belt / quiver strap
    f(4,10,8,1,D);
    f(4,11,3,3,P);   // left leg
    f(9,11,3,3,P);   // right leg
    f(4,14,3,1,Br);  // boots
    f(9,14,3,1,Br);
    f(2,3,1,11,Br);  // bow stave
    f(1,3,1,1,Br);f(1,13,1,1,Br); // bow tips
    f(3,8,5,1,Br);   // arrow shaft
    f(3,8,1,1,G);    // arrowhead
    f(12,3,1,1,Br);  // quiver
    f(12,3,1,5,D);   // quiver body
  },

  paladin(f, P, D) {
    f(6,0,4,1,Rd);   // plume
    f(5,1,6,1,G);    // helm top
    f(4,2,8,3,G);    // helm body
    f(5,3,6,1,S);    // visor
    f(6,3,1,1,K);f(9,3,1,1,K);
    f(2,5,3,3,G);    // large left pauldron
    f(11,5,3,3,G);   // large right pauldron
    f(4,5,8,5,P);    // chest
    f(6,6,4,3,G);    // breastplate
    f(8,6,1,3,D);    // cross on breast V
    f(6,8,4,1,D);    // cross on breast H
    f(4,10,8,1,Au);  // belt
    f(4,11,3,3,G);   // left greave
    f(9,11,3,3,G);   // right greave
    f(1,6,3,5,D);    // shield face
    f(1,6,3,1,Au);   // shield top
    f(2,7,2,3,P);    // shield colour
    f(2,9,2,1,Au);   // shield emblem
  },

  barbarian(f, P, D) {
    f(4,0,8,2,Br);   // wild hair top
    f(3,1,10,2,Br);  // hair sides
    f(4,2,8,4,S);    // face (wider head)
    f(5,4,1,1,K);f(10,4,1,1,K); // eyes (wider apart)
    f(6,5,4,1,Sd);   // stubble
    f(3,6,10,4,P);   // wide torso (no shirt)
    f(3,6,1,4,D);    // left arm
    f(12,6,1,4,D);   // right arm
    f(3,6,1,4,S);    // left arm skin
    f(12,6,1,4,S);   // right arm skin
    f(3,10,10,1,Br); // fur belt
    f(4,11,3,3,P);   // left leg
    f(9,11,3,3,P);   // right leg
    f(4,14,3,1,Br);  // boots
    f(9,14,3,1,Br);
    f(13,2,1,12,Br); // greataxe haft
    f(12,2,3,2,G);   // axe head
    f(11,2,2,5,G);   // axe lower blade
    f(13,2,1,5,Gd);  // axe edge
  },

  druid(f, P, D) {
    f(5,0,1,2,Gn);f(10,0,1,2,Gn);f(7,0,2,1,Gn); // antler/leaf crown
    f(5,2,6,3,S);    // face
    f(4,2,1,4,Br);f(11,2,1,4,Br); // hood/hair sides
    f(6,3,1,1,K);f(9,3,1,1,K);
    f(3,5,10,9,P);   // druid robe
    f(4,5,8,1,Br);   // hood edge
    f(5,7,2,1,Gn);   // leaf motif L
    f(9,7,2,1,Gn);   // leaf motif R
    f(6,9,4,2,Gn);   // leaf cluster chest
    f(4,11,8,1,Gn);  // vine belt
    f(5,12,2,3,P);   // legs
    f(9,12,2,3,P);
    f(2,3,1,11,Db);  // gnarled staff
    f(1,2,2,3,Gn);   // leaves at top
    f(3,2,1,1,Gn);
  },

  bard(f, P, D) {
    f(4,0,8,2,P);    // feathered hat
    f(3,2,10,1,D);   // hat brim
    f(12,0,3,2,Rd);  // feather
    f(5,3,6,3,S);    // face
    f(6,4,1,1,K);f(9,4,1,1,K);
    f(7,5,2,1,K);    // smile
    f(4,6,8,5,P);    // colorful outfit
    f(5,6,1,5,D);    // left stripe
    f(10,6,1,5,D);   // right stripe
    f(7,6,2,1,Au);   // gold neck
    f(4,11,8,1,Au);  // belt
    f(5,12,2,3,D);   // dark tights L
    f(9,12,2,3,D);   // dark tights R
    f(11,6,4,4,Br);  // lute body
    f(12,7,2,2,Db);  // lute hollow
    f(13,3,1,4,Br);  // lute neck
    f(11,9,2,2,S);   // strumming hand
  },

  monk(f, P, D) {
    f(6,1,4,1,K);    // shaved head shadow
    f(5,2,6,3,S);    // face
    f(6,3,1,1,K);f(9,3,1,1,K);
    f(7,4,2,1,K);    // calm/serene expression
    f(4,5,8,6,P);    // simple gi
    f(5,5,6,1,W);    // white collar
    f(8,5,1,6,W);    // gi fold line
    f(4,11,8,1,Au);  // rope belt
    f(7,11,2,2,Au);  // belt knot
    f(4,12,3,2,P);   // left leg
    f(9,12,3,2,P);   // right leg
    f(4,14,3,1,S);   // bare feet
    f(9,14,3,1,S);
    f(12,4,2,4,P);   // right arm raised
    f(13,3,2,2,S);   // fist
    f(2,7,2,4,P);    // left arm
    f(2,11,2,1,S);   // left hand
  },

  sorcerer(f, P, D) {
    f(4,0,8,3,D);    // tall dramatic collar
    f(5,1,6,2,P);    // collar inner
    f(5,3,6,3,S);    // face inside collar
    f(6,4,1,1,K);f(9,4,1,1,K);
    f(5,4,6,1,K);    // intense brow
    f(3,6,10,8,P);   // flowing robe
    f(2,8,12,4,P);   // robe billows
    f(5,8,2,2,Pv);   // arcane left mark
    f(9,8,2,2,Pv);   // arcane right mark
    f(7,10,2,2,Au);  // energy core
    f(2,9,3,3,Pv);   // orb in hand
    f(3,10,2,2,W);   // orb highlight
    f(5,13,2,2,P);   // legs under robe
    f(9,13,2,2,P);
  },

  warlock(f, P, D) {
    f(4,0,8,2,K);    // dark hood
    f(3,2,10,3,K);   // hood deep
    f(5,3,6,3,Sd);   // skin (shadowed)
    f(6,4,1,1,Pv);   // glowing eye L
    f(9,4,1,1,Pv);   // glowing eye R
    f(4,5,8,8,K);    // dark outer robe
    f(5,6,6,6,P);    // inner robe
    f(8,7,1,3,Pv);   // eldritch symbol V
    f(7,9,3,1,Pv);   // eldritch symbol H
    f(7,9,1,1,W);    // eye in symbol
    f(1,8,3,4,Db);   // grimoire cover
    f(1,8,3,1,Au);   // grimoire clasp
    f(2,9,2,2,W);    // grimoire pages
    f(12,8,1,4,K);   // eldritch chain
    f(13,9,2,1,K);
    f(11,11,2,1,K);
    f(5,12,2,3,K);   // legs (dark robe)
    f(9,12,2,3,K);
  },

  artificer(f, P, D) {
    f(4,1,8,2,D);    // goggles strap
    f(4,2,3,2,Au);   // left goggle
    f(9,2,3,2,Au);   // right goggle
    f(7,2,2,2,W);    // goggle lenses
    f(5,4,6,2,S);    // lower face
    f(7,5,2,1,Br);   // beard goatee
    f(3,6,10,5,P);   // coat/vest
    f(4,7,2,2,Au);   // gear on coat L
    f(10,7,2,2,Au);  // gear on coat R
    f(4,11,8,1,Br);  // belt with pouches
    f(4,12,3,3,P);   // left leg
    f(9,12,3,3,P);   // right leg
    f(4,14,3,1,D);   // boots
    f(9,14,3,1,D);
    f(12,5,1,1,Au);  // wrench top
    f(12,6,3,1,G);   // wrench handle
    f(13,7,1,6,G);   // wrench shaft
  },

  default(f, P, D) {
    // Generic adventurer — simple but clean
    f(5,1,6,4,S);    // head
    f(6,2,1,1,K);f(9,2,1,1,K);
    f(7,3,2,1,K);    // smile
    f(4,5,8,5,P);    // body
    f(4,10,8,1,D);   // belt
    f(4,11,3,3,P);   // left leg
    f(9,11,3,3,P);   // right leg
    f(4,14,3,1,D);f(9,14,3,1,D); // boots
  },
};

// Use the same sprite for blood hunter and other unknown classes
DEFS['blood hunter'] = DEFS.barbarian;
DEFS.other           = DEFS.default;

// ── Rendering ─────────────────────────────────────────────────────────────

const _cache = new Map();

/**
 * Returns a cached HTMLCanvasElement with the class sprite drawn on it.
 * @param {string} className  e.g. 'Fighter', 'Wizard'
 * @param {string} colour     hex colour string, e.g. '#c084fc'
 * @param {number} size       canvas pixel size (default 64)
 */
export function renderSprite(className, colour, size = 64) {
  const key = `${className}|${colour}|${size}`;
  if (_cache.has(key)) return _cache.get(key);
  if (typeof document === 'undefined') return null;

  const canvas = document.createElement('canvas');
  canvas.width  = size;
  canvas.height = size;
  const ctx = canvas.getContext('2d');
  ctx.imageSmoothingEnabled = false;

  const scale = size / 16;
  const P = colour;
  const D = darken(colour, 0.48);

  // fill helper in 16×16 logical coords
  const f = (x, y, w, h, color) => {
    ctx.fillStyle = color;
    ctx.fillRect(
      Math.round(x * scale), Math.round(y * scale),
      Math.round(w * scale), Math.round(h * scale),
    );
  };

  const normalised = (className || '').toLowerCase().trim();
  const drawFn = DEFS[normalised] || DEFS.default;
  drawFn(f, P, D);

  // Circular clip — masks sprite to a circle matching token ring
  ctx.globalCompositeOperation = 'destination-in';
  ctx.beginPath();
  ctx.arc(size / 2, size / 2, size / 2 - 1, 0, Math.PI * 2);
  ctx.fillStyle = '#000';
  ctx.fill();
  ctx.globalCompositeOperation = 'source-over';

  _cache.set(key, canvas);
  return canvas;
}

/** Clear the sprite cache (call if colour changes at runtime). */
export function clearSpriteCache() { _cache.clear(); }


// ── Object sprite definitions ──────────────────────────────────────────────
// Same grid/palette conventions.  P = object's color, D = dark P.
// Additional fixed colours:
//   Gy = mid grey (#888898)   Bl = blue water (#3a8abf)
//   Fr = fire orange (#e06010) Fg = fire yellow (#f5c020)
//   Iv = stone/ivory (#c8c0b0) Ivy = dark stone (#8a8478)
//   Gld = gold (#c89820)       Ir = iron (#606070)

const Gy  = '#888898';
const Bl  = '#3a8abf';
const Fr  = '#e06010';
const Fg  = '#f5c020';
const Iv  = '#c8c0b0';
const Ivy = '#8a8478';
const Gld = '#c89820';
const Ir  = '#606070';
const Tl  = '#2a6a2a'; // tree leaf green
const Td  = '#1a4a1a'; // tree leaf dark

const OBJ_DEFS = {

  barrel(f, P, D) {
    f(4,1,8,13,P);          // body
    f(3,3,10,1,D);          // top band
    f(3,7,10,1,D);          // mid band
    f(3,11,10,1,D);         // bot band
    f(4,1,8,2,D);           // top face
    f(4,13,8,1,D);          // base
    f(5,2,6,1,Br);          // top ring
    f(5,12,6,1,Br);         // bot ring
    f(7,4,2,3,D);           // stave line L
    f(11,4,1,3,D);          // stave line R
  },

  crate(f, P, D) {
    f(2,2,12,12,P);         // face
    f(2,2,12,1,D);          // top edge
    f(2,13,12,1,D);         // bot edge
    f(2,2,1,12,D);          // left edge
    f(13,2,1,12,D);         // right edge
    f(7,2,1,12,D);          // centre V plank line
    f(2,7,12,1,D);          // centre H plank line
    f(5,5,2,2,Db);          // nail top-left
    f(9,5,2,2,Db);          // nail top-right
    f(5,10,2,2,Db);         // nail bot-left
    f(9,10,2,2,Db);         // nail bot-right
  },

  chest(f, P, D) {
    f(2,5,12,8,P);          // chest body
    f(2,4,12,2,D);          // lid face (slightly darker)
    f(2,4,12,1,Br);         // lid top edge
    f(2,4,1,8,D);           // left wall
    f(13,4,1,8,D);          // right wall
    f(2,12,12,1,D);         // base
    f(7,5,2,1,Gld);         // lock plate top
    f(6,6,4,3,Gld);         // lock plate
    f(7,7,2,1,K);           // keyhole
    f(2,5,4,1,Gld);         // hinge left
    f(10,5,4,1,Gld);        // hinge right
  },

  table(f, P, D) {
    f(2,4,12,3,P);          // table top
    f(2,4,12,1,D);          // far edge (depth)
    f(2,4,1,3,D);           // left edge
    f(13,4,1,3,D);          // right edge
    f(3,7,2,6,D);           // leg front-left
    f(11,7,2,6,D);          // leg front-right
    f(4,7,8,1,P);           // apron
  },

  chair(f, P, D) {
    f(4,2,8,3,D);           // backrest
    f(4,2,1,3,P);           // backrest left post
    f(11,2,1,3,P);          // backrest right post
    f(5,3,6,1,P);           // backrest rail
    f(3,5,10,3,P);          // seat
    f(3,5,10,1,D);          // seat top edge
    f(4,8,2,5,D);           // leg front-left
    f(10,8,2,5,D);          // leg front-right
    f(5,8,6,1,D);           // seat bottom edge
  },

  bookshelf(f, P, D) {
    f(2,1,12,14,D);         // frame back
    f(2,1,1,14,P);          // left side
    f(13,1,1,14,P);         // right side
    f(3,4,10,1,P);          // middle shelf
    f(3,8,10,1,P);          // lower shelf
    f(3,12,10,1,P);         // base shelf
    // Books on top shelf
    f(3,1,2,3,Rd);f(5,1,2,3,Au);f(7,1,2,3,Pv);f(9,1,2,3,Gn);f(11,1,2,3,Bl);
    // Books on middle shelf
    f(3,5,3,3,Au);f(6,5,2,3,Rd);f(8,5,3,3,Pv);f(11,5,2,3,Br);
    // Books on lower shelf
    f(3,9,2,3,Gn);f(5,9,3,3,Bl);f(8,9,2,3,Au);f(10,9,3,3,Rd);
  },

  bed(f, P, D) {
    f(1,1,14,14,P);         // bed frame
    f(1,1,14,1,D);          // headboard top
    f(1,12,14,2,D);         // footboard
    f(1,1,1,14,D);          // left side
    f(14,1,1,14,D);         // right side
    f(2,2,12,10,W);         // mattress/sheet
    f(2,2,5,4,W);           // pillow
    f(2,2,5,4,Gy);          // pillow shading
    f(3,3,3,2,W);           // pillow highlight
    f(2,6,12,6,D);          // blanket area (darker sheet)
    f(2,6,12,1,P);          // blanket fold
  },

  throne(f, P, D) {
    f(3,1,10,3,D);          // high back
    f(3,1,1,12,P);          // left arm
    f(12,1,1,12,P);         // right arm
    f(4,2,8,2,Gld);         // back gold inlay
    f(4,1,8,1,Gld);         // crown top
    f(5,0,2,1,Gld);f(9,0,2,1,Gld); // crown points
    f(4,4,8,6,Rd);          // red cushion seat
    f(5,5,6,4,Rd);
    f(4,4,8,1,D);           // cushion top shadow
    f(4,10,8,3,P);          // front panel
    f(4,10,8,1,Gld);        // gold trim
    f(5,11,2,1,Gld);f(9,11,2,1,Gld); // decorative nails
  },

  boulder(f, P, D) {
    f(4,2,8,1,P);           // top
    f(2,3,12,2,P);          // upper body
    f(1,5,14,6,P);          // main mass
    f(2,11,12,2,P);         // lower body
    f(4,13,8,1,P);          // base
    f(3,3,3,2,W);           // highlight top-left
    f(5,5,2,1,W);           // highlight mid
    f(3,5,1,4,D);           // shadow left
    f(11,7,3,4,D);          // shadow right
    f(4,12,8,1,D);          // base shadow
  },

  pillar(f, P, D) {
    f(4,0,8,2,Iv);          // capital top
    f(3,1,10,2,Iv);         // capital flare
    f(5,3,6,9,P);           // shaft
    f(4,3,1,9,D);           // shaft shadow left
    f(5,3,6,1,W);           // shaft highlight top
    f(3,12,10,2,Iv);        // base flare
    f(4,13,8,2,Iv);         // base
    f(6,3,2,9,W);           // highlight stripe
  },

  statue(f, P, D) {
    f(5,1,6,3,Iv);          // head
    f(6,2,1,1,Ivy);f(9,2,1,1,Ivy); // carved eyes
    f(4,4,8,5,Iv);          // robed body
    f(3,4,1,5,Ivy);         // left arm
    f(12,4,1,5,Ivy);        // right arm
    f(3,4,2,2,Iv);          // left arm detail
    f(11,4,2,2,Iv);         // right arm detail
    f(4,9,3,5,Iv);          // left leg
    f(9,9,3,5,Iv);          // right leg
    f(3,13,10,2,D);         // base plinth
    f(5,1,6,1,Ivy);         // hair/crown line
  },

  portcullis(f, P, D) {
    f(2,1,2,14,Ir);         // left bar V
    f(7,1,2,14,Ir);         // mid bar V
    f(12,1,2,14,Ir);        // right bar V
    f(2,3,12,2,Ir);         // bar H top
    f(2,8,12,2,Ir);         // bar H mid
    f(2,13,12,2,Ir);        // bar H bot
    f(3,1,1,14,D);          // bar shadow V left
    f(8,1,1,14,D);          // bar shadow V mid
    f(13,1,1,14,D);         // bar shadow V right
    f(2,5,12,1,D);          // bar shadow H top
    f(2,10,12,1,D);         // bar shadow H mid
    // Spike tips
    f(2,0,2,2,Gd);f(7,0,2,2,Gd);f(12,0,2,2,Gd);
  },

  altar(f, P, D) {
    f(1,4,14,8,P);          // stone top surface
    f(1,4,14,1,W);          // top highlight
    f(1,4,1,8,D);           // left shadow
    f(14,4,1,8,D);          // right shadow
    f(1,11,14,1,D);         // bottom shadow
    f(2,10,12,2,D);         // front face
    // Carved symbol
    f(7,5,2,1,Au);f(8,5,1,3,Au);f(7,7,2,1,Au); // cross
    f(6,6,4,1,Au);
    // Candles
    f(3,3,1,2,W);f(12,3,1,2,W);
    f(3,2,1,1,Fr);f(12,2,1,1,Fr);
    f(2,5,3,1,Gld);f(11,5,3,1,Gld); // candle base
  },

  rubble(f, P, D) {
    f(1,9,4,4,P);           // rock BL
    f(5,11,3,3,D);          // rock BC
    f(9,10,5,4,P);          // rock BR
    f(3,5,5,5,D);           // rock TL
    f(8,6,4,5,P);           // rock TR
    f(6,3,3,4,Iv);          // rock top
    f(2,9,2,2,W);           // highlight BL
    f(9,6,2,2,W);           // highlight TR
    f(6,4,1,2,W);           // highlight top
  },

  tree(f, P, D) {
    f(5,1,6,3,Tl);          // top tuft
    f(3,3,10,3,Tl);         // mid canopy
    f(2,5,12,4,P);          // lower canopy main
    f(1,7,14,3,D);          // base canopy shadow
    f(4,5,4,2,Tl);          // highlight L
    f(3,3,2,2,Tl);          // highlight TL
    f(6,10,4,4,Br);         // trunk
    f(7,10,2,4,Db);         // trunk shadow
  },

  large_tree(f, P, D) {
    f(6,0,4,2,Tl);          // crown peak
    f(4,1,8,3,Tl);          // upper canopy
    f(2,3,12,4,P);          // main canopy
    f(1,5,14,4,P);          // wide canopy
    f(2,8,12,3,D);          // lower shadow
    f(3,3,3,2,Tl);f(10,3,3,2,Tl); // highlights
    f(5,11,6,5,Br);         // trunk wide
    f(7,11,2,5,Db);         // trunk shadow
    f(4,14,8,1,Db);         // base shadow
  },

  well(f, P, D) {
    f(2,6,12,8,Iv);         // stone base ring
    f(3,6,10,8,D);          // interior dark
    f(4,8,8,6,K);           // water darkness
    f(2,6,12,2,Iv);         // top rim
    f(2,6,12,1,W);          // rim highlight
    f(6,1,1,6,Br);          // left post
    f(9,1,1,6,Br);          // right post
    f(5,1,6,1,Br);          // crossbeam
    f(7,2,2,4,Db);          // rope
    f(6,6,4,2,Bl);          // water glint
  },

  fountain(f, P, D) {
    f(2,8,12,6,Iv);         // basin walls
    f(3,9,10,5,Bl);         // water fill
    f(4,9,8,1,W);           // water surface highlight
    f(2,8,12,1,W);          // basin rim highlight
    f(6,2,4,7,Iv);          // centre column
    f(5,1,6,2,P);           // top cap
    f(7,4,2,4,Bl);          // water falling down column
    f(5,7,6,2,Bl);          // spray base
    f(4,6,2,1,Bl);f(10,6,2,1,Bl); // spray L/R
  },

  cart(f, P, D) {
    f(2,4,12,7,P);          // cart body
    f(2,4,12,1,D);          // top edge
    f(2,10,12,1,D);         // bottom edge
    f(2,4,1,7,D);           // left wall
    f(13,4,1,7,D);          // right wall
    f(3,5,10,5,D);          // interior
    f(1,11,5,3,Ir);         // wheel left (iron rim)
    f(2,12,3,1,W);          // wheel left highlight
    f(10,11,5,3,Ir);        // wheel right
    f(11,12,3,1,W);         // wheel right highlight
    f(6,11,4,1,Br);         // axle
  },

  wagon(f, P, D) {
    f(1,3,14,8,P);          // wagon body
    f(1,3,14,1,D);          // top rail
    f(1,10,14,1,D);         // bottom rail
    f(1,3,1,8,D);f(14,3,1,8,D);   // side walls
    f(2,4,12,6,D);          // interior
    f(3,4,4,1,P);f(9,4,4,1,P);    // board planks top
    f(3,7,4,1,P);f(9,7,4,1,P);    // board planks mid
    f(0,11,5,4,Ir);         // wheel front-left
    f(1,12,3,2,W);          // wheel highlight
    f(11,11,5,4,Ir);        // wheel front-right
    f(12,12,3,2,W);
    f(5,12,6,1,Br);         // axle
  },

  gravestone(f, P, D) {
    f(5,1,6,2,Iv);          // rounded top
    f(4,2,8,9,Iv);          // stone body
    f(4,2,8,1,W);           // highlight top
    f(4,2,1,9,D);           // shadow left
    f(11,2,1,9,D);          // shadow right
    f(4,11,8,3,D);          // base/ground
    f(7,4,2,1,Ivy);         // cross V top
    f(7,4,2,5,Ivy);         // cross V
    f(5,6,6,1,Ivy);         // cross H
    f(5,8,4,2,Ivy);         // engraving lines
  },

  campfire(f, P, D) {
    f(3,11,10,3,Gy);        // stone ring
    f(5,11,6,3,D);          // stone ring inner
    f(5,9,2,4,Br);          // log L
    f(9,9,2,4,Br);          // log R
    f(7,8,2,5,Db);          // log cross
    f(6,4,4,5,Fr);          // flame main
    f(7,2,2,4,Fg);          // flame bright core
    f(5,5,2,3,Fr);          // flame wisp L
    f(9,5,2,3,Fr);          // flame wisp R
    f(7,3,2,2,W);           // flame tip bright
  },

  torch(f, P, D) {
    f(7,10,2,5,Br);         // handle lower
    f(7,6,2,5,Db);          // handle upper / wrap
    f(6,4,4,3,Fr);          // flame base
    f(7,2,2,4,Fg);          // flame core
    f(6,3,2,2,Fr);          // flame L
    f(8,3,2,2,Fr);          // flame R
    f(7,2,2,2,W);           // flame tip
    f(6,7,4,1,Au);          // torch head band
  },

  brazier(f, P, D) {
    f(5,9,6,4,Ir);          // bowl rim
    f(4,10,8,3,P);          // bowl body
    f(5,11,6,2,D);          // bowl interior
    f(3,13,10,1,Ir);        // base ring
    f(5,13,6,2,D);          // legs
    f(4,8,8,2,Fr);          // fire base
    f(6,5,4,4,Fr);          // flame
    f(7,3,2,4,Fg);          // flame bright
    f(6,4,2,2,Fr);          // flame L
    f(8,4,2,2,Fr);          // flame R
    f(7,3,2,2,W);           // tip
  },

  cage(f, P, D) {
    f(2,1,2,14,Ir);         // bar V L
    f(7,1,2,14,Ir);         // bar V M
    f(12,1,2,14,Ir);        // bar V R
    f(2,1,12,2,D);          // top frame
    f(2,13,12,2,D);         // bot frame
    f(2,5,12,2,Ir);         // bar H top
    f(2,10,12,2,Ir);        // bar H bot
    f(3,1,1,14,K);f(8,1,1,14,K);f(13,1,1,14,K); // bar shadows
    f(6,0,4,2,D);           // top lock
    f(7,0,2,3,Gld);         // lock plate
    f(7,1,2,1,K);           // keyhole
  },

  cauldron(f, P, D) {
    f(3,5,10,8,P);          // cauldron body
    f(2,7,12,6,P);          // wider belly
    f(2,7,12,1,D);          // belly shadow line
    f(3,5,10,1,D);          // rim
    f(1,9,14,3,D);          // shadow underside
    f(3,6,10,1,W);          // rim highlight
    f(4,7,8,6,K);           // interior dark
    f(5,7,6,2,Bl);          // brew liquid
    f(4,6,8,1,Gn);          // liquid surface
    f(5,5,6,2,Fr);          // boil bubbles (froth/fire hint)
    f(2,12,5,2,D);          // leg left
    f(9,12,5,2,D);          // leg right
  },

  forge(f, P, D) {
    f(1,6,14,8,Iv);         // stone forge body
    f(2,6,12,7,P);          // forge face
    f(1,6,14,1,W);          // top highlight
    f(5,3,6,4,K);           // forge opening dark
    f(5,4,6,3,Fr);          // fire in opening
    f(6,4,4,3,Fg);          // fire bright core
    f(2,10,5,4,P);          // anvil left leg area
    f(9,9,5,5,Ir);          // anvil
    f(8,8,6,2,Gd);          // anvil horn
    f(9,9,5,1,W);           // anvil highlight
    f(2,13,6,1,K);          // shadow L
    f(9,13,5,1,K);          // shadow R
  },

  trap(f, P, D) {
    f(3,6,10,4,Ir);         // trap body (closed)
    f(3,6,10,1,D);          // top jaw
    f(3,9,10,1,D);          // bot jaw
    f(4,6,2,4,D);           // jaw teeth L
    f(7,6,2,4,D);           // jaw teeth M
    f(10,6,2,4,D);          // jaw teeth R
    f(3,7,10,2,K);          // jaw gap dark
    f(5,7,6,2,Rd);          // trigger plate
    f(7,7,2,2,W);           // trigger plate highlight
    f(2,10,12,1,D);         // base plate
    f(4,10,2,3,Ir);         // spring L
    f(10,10,2,3,Ir);        // spring R
    f(5,12,6,2,D);          // chain/anchor
  },

  stairs(f, P, D) {
    // Side-view straight staircase ascending upward
    f(2,2,12,12,P);         // base floor area
    // Steps from bottom to top — each narrower and higher
    f(2,12,12,2,D);         // step 1 (bottom, widest)
    f(2,12,12,1,G);         // step 1 tread
    f(3,10,11,2,D);         // step 2
    f(3,10,11,1,G);         // step 2 tread
    f(4,8,10,2,D);          // step 3
    f(4,8,10,1,G);          // step 3 tread
    f(5,6,9,2,D);           // step 4
    f(5,6,9,1,G);           // step 4 tread
    f(6,4,8,2,D);           // step 5
    f(6,4,8,1,G);           // step 5 tread
    f(7,2,7,2,D);           // step 6 (top)
    f(7,2,7,1,G);           // step 6 tread
    // Left railing
    f(2,2,1,12,K);          // rail post left
    f(13,2,1,4,K);          // rail post right top
    // Arrow indicating direction
    f(9,3,2,1,W);           // arrow tip
    f(8,4,4,1,W);           // arrow base
  },

  stairs_long(f, P, D) {
    // 1×2 staircase — 16×32 grid, steps ascending bottom to top
    f(1, 26, 14, 4, P);      // bottom landing
    f(1, 26, 14, 1, G);      // landing tread
    f(2, 22, 12, 4, D);      // step 1
    f(2, 22, 12, 1, G);
    f(3, 18, 10, 4, D);      // step 2
    f(3, 18, 10, 1, G);
    f(4, 14, 8, 4, D);       // step 3
    f(4, 14, 8, 1, G);
    f(5, 10, 6, 4, D);       // step 4
    f(5, 10, 6, 1, G);
    f(6, 6, 4, 4, D);        // step 5
    f(6, 6, 4, 1, G);
    f(1, 2, 14, 4, P);       // top landing
    f(1, 2, 14, 1, G);
    // Railings
    f(1, 2, 1, 28, K);       // left rail
    f(14, 2, 1, 28, K);      // right rail
    // Direction arrow
    f(7, 4, 2, 1, W);
    f(6, 5, 4, 1, W);
  },

  table_long(f, P, D) {
    // 1×3 long table — 16×48 grid
    f(2, 2, 12, 44, P);      // tabletop
    f(2, 2, 12, 2, D);       // top edge
    f(2, 44, 12, 2, D);      // bottom edge
    f(2, 2, 2, 44, D);       // left edge
    f(12, 2, 2, 44, D);      // right edge
    // Legs
    f(3, 4, 2, 2, K);
    f(11, 4, 2, 2, K);
    f(3, 42, 2, 2, K);
    f(11, 42, 2, 2, K);
    // Center runner
    f(5, 6, 6, 36, D);
    f(6, 8, 4, 32, G);       // lighter center
  },

  spiral_stairs(f, P, D) {
    // Top-down spiral staircase — wedge-shaped steps around a centre pillar
    f(2,2,12,12,D);         // dark well background

    // Wedge steps spiralling clockwise (alternating light/dark treads)
    // Top wedge (pointing right)
    f(8,2,6,2,G);  f(8,4,4,2,G);  f(8,6,2,1,G);
    // Right wedge (pointing down)
    f(12,8,2,6,Gd); f(10,8,2,4,Gd); f(9,8,1,2,Gd);
    // Bottom wedge (pointing left)
    f(2,12,6,2,G);  f(4,10,4,2,G);  f(6,9,2,1,G);
    // Left wedge (pointing up)
    f(2,2,2,6,Gd);  f(4,4,2,4,Gd);  f(6,6,1,2,Gd);

    // Step edge lines for definition
    f(8,2,6,1,W);           // top step edge
    f(13,8,1,6,W);          // right step edge
    f(2,13,6,1,W);          // bottom step edge
    f(2,2,1,6,W);           // left step edge
    f(8,6,2,1,P);           // inner top edge
    f(9,8,1,2,P);           // inner right edge
    f(6,9,2,1,P);           // inner bottom edge
    f(6,6,1,2,P);           // inner left edge

    // Centre pillar (round look)
    f(6,6,4,4,Ir);          // pillar outer
    f(7,7,2,2,K);           // pillar core

    // Outer wall ring
    f(2,1,12,1,K);          // top wall
    f(2,14,12,1,K);         // bot wall
    f(1,2,1,12,K);          // left wall
    f(14,2,1,12,K);         // right wall
  },
};

// ── Object sprite renderer ─────────────────────────────────────────────────

/**
 * Returns a cached HTMLCanvasElement with the object sprite drawn on it.
 * @param {string} objectTypeId  e.g. 'barrel', 'chest'
 * @param {string} colour        hex colour from OBJECT_TYPES definition
 * @param {number} size          canvas pixel size (default 64)
 */
export function renderObjectSprite(objectTypeId, colour, size = 64, heightCells = 1) {
  const h = size * heightCells;
  const key = `obj:${objectTypeId}|${colour}|${size}|${heightCells}`;
  if (_cache.has(key)) return _cache.get(key);
  if (typeof document === 'undefined') return null;

  const canvas = document.createElement('canvas');
  canvas.width  = size;
  canvas.height = h;
  const ctx = canvas.getContext('2d');
  ctx.imageSmoothingEnabled = false;

  const scale = size / 16;
  const P = colour;
  const D = darken(colour, 0.5);

  const f = (x, y, w, ht, color) => {
    ctx.fillStyle = color;
    ctx.fillRect(
      Math.round(x * scale), Math.round(y * scale),
      Math.round(w * scale), Math.round(ht * scale),
    );
  };

  const drawFn = OBJ_DEFS[objectTypeId] || null;
  if (drawFn) drawFn(f, P, D);

  // Clip — circle for square, rounded rect for rectangular
  ctx.globalCompositeOperation = 'destination-in';
  if (heightCells === 1) {
    ctx.beginPath();
    ctx.arc(size / 2, size / 2, size / 2 - 1, 0, Math.PI * 2);
  } else {
    const r = size * 0.15;
    ctx.beginPath();
    ctx.roundRect(1, 1, size - 2, h - 2, r);
  }
  ctx.fillStyle = '#000';
  ctx.fill();
  ctx.globalCompositeOperation = 'source-over';

  _cache.set(key, canvas);
  return canvas;
}
